import * as React from 'react';
import clsx from 'clsx';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Navigate, useLocation } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';

import {
  Action,
  Resource,
  Scope,
  LoggedInAgencyUserFieldsFragment,
  Permission
} from '../api/graphql';

/**
 * A partial permission check that only requires resource and action.
 * Used for checking if a user has any permission for a resource/action regardless of scope.
 */
export type PermissionCheck = {
  resource: Resource;
  action: Action;
};

type PermissionsContextValue = {
  permissions: Permission[];
  hasPermission: (resource: Resource, action: Action) => boolean;
  hasScopedPermission: (scope: Scope, resource: Resource, action: Action) => boolean;
  hasAnyPermission: (checks: Array<PermissionCheck>) => boolean;
  hasAllPermissions: (checks: Array<PermissionCheck>) => boolean;
};

const PermissionsContext = React.createContext<PermissionsContextValue | null>(null);

/**
 * Provides permissions context to child components.
 * Allows child components to access the permissions for the current user using the usePermissions hook.
 */
export const PermissionsProvider: React.FC<{
  agencyUser: LoggedInAgencyUserFieldsFragment;
  children: React.ReactNode;
}> = ({ agencyUser, children }) => {
  const permissions = agencyUser.permissions || [];

  const hasPermission = React.useCallback(
    (resource: Resource, action: Action) => {
      return permissions.some((p) => p.resource === resource && p.action === action);
    },
    [permissions]
  );

  const hasScopedPermission = React.useCallback(
    (scope: Scope, resource: Resource, action: Action) => {
      return permissions.some(
        (p) => p.scope === scope && p.resource === resource && p.action === action
      );
    },
    [permissions]
  );

  const hasAnyPermission = React.useCallback(
    (checks: Array<{ resource: Resource; action: Action }>) => {
      return checks.some(({ resource, action }) => hasPermission(resource, action));
    },
    [hasPermission]
  );

  const hasAllPermissions = React.useCallback(
    (checks: Array<{ resource: Resource; action: Action }>) => {
      return checks.every(({ resource, action }) => hasPermission(resource, action));
    },
    [hasPermission]
  );

  const value = React.useMemo(
    () => ({
      permissions,
      hasPermission,
      hasScopedPermission,
      hasAnyPermission,
      hasAllPermissions
    }),
    [permissions, hasPermission, hasScopedPermission, hasAnyPermission, hasAllPermissions]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

/**
 * Hook to access permissions context.
 * Allows components to access the permissions context and values associated with it implicitly.
 *
 * @example
 * const { hasPermission } = usePermissions();
 * if (hasPermission(Resource.BusinessPolicy, Action.Create)) {
 *   // User can create policies
 * }
 */
/** Shared by every archive, restore and bulk-edit surface for workspaces and artifacts. */
export const UPDATE_CARA_WORKSPACE: PermissionCheck[] = [
  { resource: Resource.CaraWorkspace, action: Action.Update }
];

/** Shared by every surface that starts a workspace. */
export const CREATE_CARA_WORKSPACE: PermissionCheck[] = [
  { resource: Resource.CaraWorkspace, action: Action.Create }
];

export const CREATE_OUTBOUND_CALL: PermissionCheck[] = [
  { resource: Resource.OutboundCall, action: Action.Create }
];

export const usePermissions = () => {
  const context = React.useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }

  return context;
};

const DENIED_TOOLTIP = "You don't have permission to do this.";

/**
 * Button for a permissioned action. It disables itself (or hides) when the
 * caller cannot perform it, and a link button (one with `href`) loses the href
 * rather than staying clickable, since an anchor ignores `disabled`.
 */
export const PermissionButton: React.FC<
  React.ComponentProps<typeof Button> & {
    /** The action this button performs. */
    permissions: PermissionCheck[];
    mode?: 'all' | 'any';
    /**
     * `disable` (the default) keeps the control visible so the page's shape does
     * not change between roles; `hide` removes it, for actions whose absence is
     * less confusing than their presence.
     */
    whenDenied?: 'disable' | 'hide';
  }
> = ({ permissions, mode = 'all', whenDenied = 'disable', ...props }) => {
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const allowed = mode === 'all' ? hasAllPermissions(permissions) : hasAnyPermission(permissions);

  if (allowed) return <Button {...props} />;
  if (whenDenied === 'hide') return null;

  const locked = { ...props } as React.ComponentProps<typeof Button> & { href?: string };
  delete locked.href;

  return <Button {...locked} disabled title={DENIED_TOOLTIP} />;
};

/**
 * Notice explaining what the caller cannot do on this page. The caller decides
 * when to show it and what it says, because "you may upload but not create" is
 * as real a state as "you may only look".
 */
export const PermissionNotice: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className
}) => (
  <div
    className={clsx(
      'flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3',
      'dark:border-blue-900 dark:bg-blue-950/40',
      className
    )}
  >
    <LockClosedIcon className="mt-0.5 size-4 shrink-0 text-blue-700 dark:text-blue-500" />
    <p className="text-sm text-blue-900 dark:text-blue-200">{children}</p>
  </div>
);

/**
 * Component that conditionally renders children based on multiple permission checks.
 *
 * @example
 * <RequirePermissions
 *   permissions={[
 *     { resource: Resource.Integration, action: Action.Read },
 *     { resource: Resource.Integration, action: Action.Update }
 *   ]}
 * >
 *   <IntegrationSettings />
 * </RequirePermissions>
 */
export const RequirePermissions: React.FC<{
  permissions: Array<PermissionCheck>;
  mode?: 'any' | 'all';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permissions, mode = 'all', children, fallback = null }) => {
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const allowed = mode === 'all' ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  return allowed ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that passes multiple permission check results to a render function.
 *
 * @example
 * <WithPermissions
 *   permissions={[
 *     { resource: Resource.BusinessPolicy, action: Action.Create },
 *     { resource: Resource.BusinessPolicy, action: Action.Update }
 *   ]}
 * >
 *   {(allowed) => (
 *     <Button disabled={!allowed}>Manage Policies</Button>
 *   )}
 * </WithPermissions>
 */
export const WithPermissions: React.FC<{
  permissions: Array<PermissionCheck>;
  mode?: 'any' | 'all';
  children: (allowed: boolean) => React.ReactNode;
}> = ({ permissions, mode = 'any', children }) => {
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const allowed = mode === 'any' ? hasAnyPermission(permissions) : hasAllPermissions(permissions);
  return <>{children(allowed)}</>;
};

/**
 * Route protection component that redirects users without required permissions.
 * Allows route elements to be conditionally rendered based on the user's permissions.
 *
 * @example
 * {
 *   path: 'settings/subscription',
 *   element: (
 *     <ProtectedRoute permissions={[{ resource: Resource.Subscription, action: Action.Read }]}>
 *       <SettingsSubscriptionPage />
 *     </ProtectedRoute>
 *   )
 * }
 */
export const ProtectedRoute: React.FC<{
  permissions: Array<PermissionCheck>;
  mode?: 'any' | 'all';
  children: React.ReactNode;
  redirectTo?: string;
}> = ({ permissions, mode = 'all', children, redirectTo = '/app/home' }) => {
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const allowed = mode === 'all' ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  const location = useLocation();

  if (!allowed) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
